import React, { useRef, useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Button, MoneyInput } from '@refera/ui-web'
import { Grid, Typography, TextField, Tooltip } from '@material-ui/core'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'

import { getLaborTypes } from '_modules/labor-type/actions'
import { getInspectionBudgetResolutionMaterialUnids } from '_/modules/inspectionBudget/actions'
import { getInspectionBudgetResolutionMaterialUnidsSelector } from '_/modules/inspectionBudget/selector'
import { Select, BasicInput, Checkbox } from '_/components/inputs'
import AccordionInfo from '_/components/accordion/accordion-info'
import Accordion from '_/views/finance/components/Accordion'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'

import useStyles from './styles'
import { complexityOptions, menuProps } from './constants'

export function ServiceForm({
  service,
  handleCreate = () => {},
  setModalOpen,
  handleDeleteProblem,
  serviceSelectId,
  onDelete = () => {},
  isDeleting = false,
}) {
  const styles = useStyles()
  const formRef = useRef()
  const dispatch = useDispatch()
  const { results: laborTypes } = useSelector(state => state.laborTypes)
  const materialUnids = useSelector(getInspectionBudgetResolutionMaterialUnidsSelector)

  useEffect(() => {
    dispatch(getLaborTypes())
  }, [dispatch])

  useEffect(() => {
    if (!materialUnids) {
      dispatch(getInspectionBudgetResolutionMaterialUnids())
    }
  }, [dispatch, materialUnids])

  const { control, handleSubmit, setValue, watch, register, errors, clearErrors, trigger } =
    useFormContext()

  const problemsWatch = watch('problems')
  const laborTypeIdWatch = useWatch({ name: 'laborTypeId', control })
  const isExecutionTimePerSqmWatch = useWatch({ name: 'isExecutionTimePerSqm', control })

  const selectedLaborType = useMemo(() => {
    return laborTypes?.find(laborType => laborType?.id === laborTypeIdWatch)
  }, [laborTypeIdWatch, laborTypes])

  const materialUnidsOptions = useMemo(() => {
    if (Array.isArray(materialUnids?.results)) return materialUnids.results
    if (Array.isArray(materialUnids)) return materialUnids
    return []
  }, [materialUnids])

  const selectedUnidValue = useMemo(() => {
    if (typeof service?.unid === 'object') return service?.unid?.id ?? service?.unid?.value ?? ''
    return service?.unid ?? ''
  }, [service?.unid])

  const renderCategories = useCallback(() => {
    if (!service?.categories?.length) {
      return (
        <Typography variant="body2" color="textSecondary">
          Nenhuma categoria vinculada
        </Typography>
      )
    }

    return service?.categories?.map((category, index) => {
      const parentName = category?.parent?.name
      const categoryName = category?.name || ''
      const displayName = parentName ? `${parentName} > ${categoryName}` : categoryName

      return (
        <AccordionInfo
          key={category?.id || index}
          type="category"
          values={{ name: displayName }}
          isFirst={index === 0}
          isEditing={false}
          handleDeleteValue={() => {}}
        />
      )
    })
  }, [service?.categories])

  useEffect(() => {
    if (!selectedLaborType?.laborTimeBySquareMeter) return

    if (isExecutionTimePerSqmWatch) {
      clearErrors('executionTime')
      trigger('executionTime')
    }
  }, [
    isExecutionTimePerSqmWatch,
    selectedLaborType?.slug,
    selectedLaborType?.laborTimeBySquareMeter,
    clearErrors,
    setValue,
  ])

  return (
    <form className={styles.categoryForm} ref={formRef} onSubmit={handleSubmit(handleCreate)}>
      <Typography className={styles.title}>
        {serviceSelectId ? 'Editar Serviço' : 'Criar Serviço'}
      </Typography>

      <Grid className={styles.sectionRowWrapper}>
        <BasicInput name="externalId" label="Código" defaultValue={service?.externalId || ''} />
      </Grid>

      <Grid className={styles.sectionRowWrapper}>
        <BasicInput
          name="name"
          label="Serviço"
          defaultValue={service?.name || ''}
          required
          rules={{
            maxLength: { value: 255, message: `Esse campo aceita no máximo 255 caracteres` },
            required: { value: true, message: 'Esse campo é obrigatório' },
          }}
        />
        <BasicInput
          name="gridDesc"
          label="Descrição da grid"
          defaultValue={service?.gridDesc || ''}
          rules={{
            maxLength: { value: 255, message: `Esse campo aceita no máximo 255 caracteres` },
          }}
        />
      </Grid>

      <Grid className={styles.sectionWrapper}>
        <Grid style={{ width: '100%' }}>
          <Typography className={styles.label}>Descrição diagnóstico</Typography>
          <TextField
            id="diagnosisDesc"
            multiline
            maxRows={5}
            name="diagnosisDesc"
            inputRef={register}
            defaultValue={service?.diagnosisDesc}
            className={styles.textfield}
            variant="standard"
            InputProps={{
              style: {
                fontSize: 16,
              },
            }}
          />
        </Grid>
        <Grid style={{ width: '100%' }}>
          <Typography className={styles.label}>Descrição Solução</Typography>
          <TextField
            id="solutionDesc"
            multiline
            maxRows={5}
            name="solutionDesc"
            inputRef={register}
            className={styles.textfield}
            variant="standard"
            InputProps={{
              style: {
                fontSize: 16,
              },
            }}
          />
        </Grid>
      </Grid>

      <Grid className={styles.sectionRowWrapper} style={{ alignItems: 'flex-end' }}>
        <Select
          label="Tipo de MO"
          name="laborTypeId"
          required
          labelClasses={styles.labelSelect}
          options={laborTypes?.toJS ? laborTypes.toJS() : laborTypes}
          defaultValue={service?.laborTypeId}
          getValue={item => item.id}
          getLabel={item => item.name}
          getKey={item => item.id}
          menuProps={menuProps}
          style={{ width: '100%' }}
        />

        <NumericInput
          name="executionTime"
          required={!isExecutionTimePerSqmWatch}
          label="Tempo de execução (em horas)"
          rules={{
            validate: value => {
              if (isExecutionTimePerSqmWatch) return true
              return value ? true : 'Esse campo é obrigatório'
            },
          }}
          placeholder="0,00"
          variant="standard"
          defaultValue={service?.executionTime}
          labelClasses={styles.labelSelect}
          inputProps={{ style: { textAlign: 'left', marginBottom: '4px' } }}
          style={{ width: '100%', marginTop: '4px' }}
          disabled={isExecutionTimePerSqmWatch}
        />

        <Select
          label="Unidade de medida"
          name="unid"
          labelClasses={styles.labelSelect}
          options={materialUnidsOptions}
          defaultValue={selectedUnidValue}
          getValue={item => item?.id ?? item?.value ?? ''}
          getLabel={item => item?.unid || item?.label || ''}
          getKey={item => item?.id ?? item?.value ?? item?.unid}
          menuProps={menuProps}
          style={{ width: '100%' }}
        />
      </Grid>
      {selectedLaborType?.laborTimeBySquareMeter && (
        <Grid className={styles.sectionRowWrapper}>
          <Checkbox
            name="isExecutionTimePerSqm"
            defaultValue={service?.isExecutionTimePerSqm ?? false}
            label={
              <span className={styles.tooltipLabel}>
                Tempo de execução por m²
                <Tooltip
                  title="Ative esta opção para que o Tempo execução deste serviço seja definido com base na metragem quadrada (m²) configurada no Tipo de MO."
                  arrow
                >
                  <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
          />
        </Grid>
      )}

      <Grid className={styles.sectionRowWrapper}>
        <MoneyInput
          ref={register('idealLabor')}
          name="idealLabor"
          label="Mão de obra ideal"
          decimalScale={2}
          value={service?.idealLabor?.replace('.', ',') || ''}
          onChange={e => setValue('idealLabor', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
        <MoneyInput
          ref={register('idealMaterial')}
          name="idealMaterial"
          label="Material ideal"
          decimalScale={2}
          value={service?.idealMaterial?.replace('.', ',') || ''}
          onChange={e => setValue('idealMaterial', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
        <MoneyInput
          ref={register('idealPrice')}
          name="idealPrice"
          label="Preço ideal"
          decimalScale={2}
          value={service?.idealPrice?.replace('.', ',') || ''}
          onChange={e => setValue('idealPrice', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
      </Grid>

      <Grid className={styles.sectionWrapper}>
        <MoneyInput
          ref={register('additionalLabor')}
          name="additionalLabor"
          label="Mão de obra adicional"
          decimalScale={2}
          value={service?.additionalLabor?.replace('.', ',') || ''}
          onChange={e => setValue('additionalLabor', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
        <Grid className={styles.textfield}>
          <></>
        </Grid>
        <MoneyInput
          ref={register('additionalPrice')}
          name="additionalPrice"
          label="Preço adicional"
          decimalScale={2}
          value={service?.additionalPrice?.replace('.', ',') || ''}
          onChange={e => setValue('additionalPrice', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
      </Grid>

      <Grid className={styles.sectionWrapper}>
        <MoneyInput
          ref={register('maxLabor')}
          name="maxLabor"
          label="Mão de obra máximo"
          decimalScale={2}
          value={service?.maxLabor?.replace('.', ',') || ''}
          onChange={e => setValue('maxLabor', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
        <MoneyInput
          ref={register('maxMaterial')}
          name="maxMaterial"
          label="Material máximo"
          decimalScale={2}
          value={service?.maxMaterial?.replace('.', ',') || ''}
          onChange={e => setValue('maxMaterial', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
        <MoneyInput
          ref={register('maxPrice')}
          name="maxPrice"
          label="Preço máximo"
          decimalScale={2}
          value={service?.maxPrice?.replace('.', ',') || ''}
          onChange={e => setValue('maxPrice', e.floatValue, { shouldDirty: true })}
          placeholder="R$ "
          className={styles.textfield}
          style={{ fontSize: '14px' }}
        />
      </Grid>

      <Grid className={styles.sectionRowWrapper}>
        <Select
          label="Complexidade"
          name="complexity"
          labelClasses={styles.labelSelect}
          options={complexityOptions}
          defaultValue={service?.complexity}
          rules={{ required: { value: true, message: 'Esse campo é obrigatório' } }}
          error={!!errors?.complexity}
          required
          getValue={item => item.value}
          getLabel={item => item.label}
          getKey={item => item.value}
          menuProps={{
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
          }}
          style={{ gridArea: 'businessFront', width: 'auto' }}
        />
        <BasicInput
          name="materialLink"
          label="Link do Material"
          defaultValue={service?.materialLink || ''}
          labelClasses={styles.labelSelect}
          rules={{
            maxLength: { value: 2000, message: `Esse campo aceita no máximo 2000 caracteres` },
          }}
        />
      </Grid>

      <Grid className={styles.sectionRowWrapper}>
        <div className={styles.checkboxContainer}>
          <Checkbox name="active" label="Ativo" defaultValue={service?.active} />

          <Checkbox
            name="techVisit"
            label="Visita técnica"
            defaultValue={service?.techVisit}
            labelProps={{ style: { whiteSpace: 'nowrap' } }}
          />
        </div>
      </Grid>

      <Accordion id="service-categories" title="Categorias vinculadas" className={styles.accordion}>
        <Grid className={styles.containerAdd}>{renderCategories()}</Grid>
      </Accordion>

      <Accordion id="problem-services" title="Problemas vinculados" className={styles.accordion}>
        <Grid className={styles.containerAdd}>
          {problemsWatch?.map((item, index) => (
            <AccordionInfo
              isFirst={index === 0}
              key={item?.id}
              values={item}
              isEditing
              handleDeleteValue={handleDeleteProblem}
            />
          ))}
          <Button
            style={{ marginTop: '20px', maxWidth: '64px' }}
            variant="contained"
            color="primary"
            onClick={() => setModalOpen(true)}
          >
            +Adicionar registro
          </Button>
        </Grid>
      </Accordion>

      {serviceSelectId && (
        <Button onClick={onDelete} disabled={isDeleting} className={styles.deleteButton}>
          Excluir
        </Button>
      )}
    </form>
  )
}
